import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdPlaceholder from '../components/AdPlaceholder';
import BedtimeDarkPrompt from '../components/BedtimeDarkPrompt/BedtimeDarkPrompt';
import SeoHead from '../components/SeoHead';
import { LetterHiveBrandIcon, WordRungsBrandIcon } from '../components/icons/BrandGameIcons';
import {
  COZY_HUB_SECTION_ORDER,
  COZY_HUB_SECTIONS,
  cozyHubGameById,
  cozyHubGamesForSection,
} from '../data/cozyHubGames';
import { hubDailyCompletion } from '../utils/hubDailyCompletion';
import { buildHubGreeting } from '../utils/hubGreeting';
import './Home.css';

function GameCardIcon({ game }) {
  if (game.iconId === 'letter-hive') {
    return <LetterHiveBrandIcon title={game.name} />;
  }
  if (game.iconId === 'word-rungs') {
    return <WordRungsBrandIcon title={game.name} />;
  }
  return <>{game.icon}</>;
}

export default function Home() {
  const { user, profile } = useAuth();

  const completion = useMemo(
    () => hubDailyCompletion(profile, Boolean(user)),
    [profile, user],
  );

  const greeting = useMemo(
    () =>
      buildHubGreeting({
        completion,
        displayName:
          profile?.displayName ||
          user?.displayName ||
          user?.email?.split('@')[0],
      }),
    [completion, profile?.displayName, user],
  );

  const recommendGame = greeting.recommendGameId
    ? cozyHubGameById(greeting.recommendGameId)
    : null;

  return (
    <main className="home">
      <BedtimeDarkPrompt />
      <SeoHead
        description="Play free cozy word games: Word Master, Letter Hive, Word Rungs, Word Square, and Hidden Words. Daily puzzles, guides, FAQ, streaks, and leaderboards."
        path="/"
      />
      <section className="home__hero">
        <img
          className="home__brand-mark"
          src={`${process.env.PUBLIC_URL}/branding/app-icon-foreground.png`}
          alt=""
          width={200}
          height={200}
          decoding="async"
          aria-hidden="true"
        />
        <h1 className="home__title">Letter Quest</h1>
        <p className="home__tagline">Your calm daily word ritual</p>
      </section>

      <section className="home__greeting" aria-labelledby="greeting-heading">
        <h2 id="greeting-heading" className="home__greeting-headline">
          {greeting.headline}
        </h2>
        {greeting.subline && (
          <p className="home__greeting-subline">{greeting.subline}</p>
        )}
        {recommendGame && (
          <Link className="home__play-next" to={recommendGame.path}>
            Play {recommendGame.name}
          </Link>
        )}
      </section>

      <div className="home__ad-hero">
        <AdPlaceholder variant="banner" />
      </div>

      <section className="home__guides" aria-labelledby="guides-heading">
        <h2 id="guides-heading">Guides &amp; help</h2>
        <p className="home__guides-text">
          New here or trying a new mode? Read the full <Link to="/how-to-play">how to play</Link> hub for rules, tips,
          and FAQs for every game, check the <Link to="/faq">FAQ</Link>, or learn <Link to="/about">about Letter Quest</Link>
          {' '}
          and how accounts, dailies, and leaderboards work.
        </p>
      </section>

      {COZY_HUB_SECTION_ORDER.map((sectionId) => {
        const section = COZY_HUB_SECTIONS[sectionId];
        const games = cozyHubGamesForSection(sectionId);
        return (
          <section
            key={sectionId}
            className="home__section"
            aria-labelledby={`section-${sectionId}`}
          >
            <h2 id={`section-${sectionId}`} className="home__section-title">
              {section.title}
            </h2>
            <p className="home__section-blurb">{section.blurb}</p>
            <div className="game-grid">
              {games.map((game) => {
                const done = Boolean(completion[game.id]);
                const recommend = greeting.recommendGameId === game.id;
                return (
                  <Link
                    key={game.id}
                    to={game.path}
                    className={`game-card${recommend ? ' game-card--recommend' : ''}${done ? ' game-card--done' : ''}`}
                  >
                    <span className="game-card__icon">
                      <GameCardIcon game={game} />
                    </span>
                    <div className="game-card__header">
                      <h3 className="game-card__name">{game.name}</h3>
                      {done && (
                        <span className="game-card__stamp" aria-label="Done today">
                          ✓
                        </span>
                      )}
                    </div>
                    <p className="game-card__desc">{game.description}</p>
                    <div className="game-card__meta">
                      <span className="game-card__chip">{game.minutesLabel}</span>
                      {recommend && (
                        <span className="game-card__chip game-card__chip--accent">
                          Play next
                        </span>
                      )}
                      {done && (
                        <span className="game-card__chip game-card__chip--done">
                          Done today
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        );
      })}
    </main>
  );
}
